import type {
  Workspace,
  WorkspaceCreateInput,
  WorkspaceModel,
  WorkspaceStore,
  WorkspaceUpdateInput
} from '@repo/common/types/Workspace';
import { getForegroundColor, getWorkspaceColorVariants } from '@repo/toolslib/src/utils/color.util';
import { derived, get, writable, type Readable } from 'svelte/store';
import { getHandlerAsync } from '../sharedlib';

// Store to track current pathname for background updates
export const currentPathname = writable<string>('');

const createWorkspaceStore = () => {
  const { subscribe, update } = writable<WorkspaceStore>({
    workspaces: new Map(),
    loading: false,
    error: null,
    selectedWorkspace: undefined,
    lastSelectedWorkspace: undefined
  });

  // Initialize the workspace model
  let model: WorkspaceModel | null = null;

  const initializeModel = async () => {
    if (!model) {
      try {
        const handler = await getHandlerAsync();
        model = await handler.getWorkspaceModel();
        if (!model) {
          throw new Error('Workspace model not initialized');
        }
        await model.sync();
      } catch (err: any) {
        console.warn('Failed to initialize workspace model:', err);
        throw err;
      }
    }
    return model;
  };

  // Refresh workspaces from database
  const refreshWorkspaces = async () => {
    try {
      update((store) => ({ ...store, loading: true, error: null }));

      const model = await initializeModel();
      if (!model) {
        return;
      }
      const allWorkspaces: Workspace[] = await model.findAllActive();
      const nonEmptyWorkspaces = allWorkspaces.filter((workspace: Workspace) => !!workspace.data);

      update((store) => ({
        ...store,
        workspaces: new Map(
          nonEmptyWorkspaces.map((workspace: Workspace) => [workspace.id, workspace])
        ),
        loading: false,
        lastSync: new Date()
      }));
    } catch (err: any) {
      console.debug('Failed to refresh workspaces:', err);
      update((store) => ({
        ...store,
        loading: false,
        error: err.message
      }));
    }
  };

  // Create a new workspace
  const createWorkspace = async (input: WorkspaceCreateInput): Promise<Workspace> => {
    try {
      update((store) => ({ ...store, loading: true, error: null }));

      const model = await initializeModel();
      if (!model) {
        update((store) => ({ ...store, loading: false, error: 'Workspace model not initialized' }));
        throw new Error('Workspace model not initialized');
      }

      // Get foreground color from predefined colors
      const iconColor = input.color ? getForegroundColor(input.color) : '#000000';

      const workspace = await model.createWorkspace({
        ...input,
        data: {
          ...input.data,
          color: input.color || null,
          icon: input.icon || null,
          note: input.note || null,
          iconColor
        }
      });

      // Update store immediately for better UX
      setWorkspace(workspace);

      return workspace;
    } catch (err: any) {
      console.warn('Failed to create workspace:', err);
      update((store) => ({
        ...store,
        loading: false,
        error: err.message
      }));
      throw err;
    }
  };

  // Update an existing workspace
  const updateWorkspace = async (id: string, input: WorkspaceUpdateInput): Promise<boolean> => {
    try {
      update((store) => ({ ...store, loading: true, error: null }));

      const model = await initializeModel();
      if (!model) {
        update((store) => ({ ...store, loading: false, error: 'Workspace model not initialized' }));
        return false;
      }

      // Get foreground color if color is being updated
      let iconColor: string | undefined;
      if (input.color) {
        iconColor = getForegroundColor(input.color);
      }

      const updatedWorkspace = await model.updateWorkspace(id, {
        ...input,
        data: {
          ...input.data,
          color: input.color || null,
          icon: input.icon || null,
          note: input.note || null,
          ...(iconColor && { iconColor })
        }
      });

      if (!updatedWorkspace) {
        update((store) => ({
          ...store,
          loading: false,
          error: 'Workspace not found'
        }));
        return false;
      }

      // Update store immediately for better UX
      setWorkspace(updatedWorkspace);

      return true;
    } catch (err: any) {
      console.warn('Failed to update workspace:', err);
      update((store) => ({
        ...store,
        loading: false,
        error: err.message
      }));
      return false;
    }
  };

  // Delete a workspace (soft delete)
  const deleteWorkspace = async (id: string): Promise<boolean> => {
    try {
      update((store) => ({ ...store, loading: true, error: null }));

      const model = await initializeModel();
      if (!model) {
        update((store) => ({ ...store, loading: false, error: 'Workspace model not initialized' }));
        return false;
      }
      const success = await model.deleteWorkspace(id);

      if (success) {
        // Update store immediately for better UX
        removeWorkspace(id);
      }

      return success;
    } catch (err: any) {
      console.warn('Failed to delete workspace:', err);
      update((store) => ({
        ...store,
        loading: false,
        error: err.message
      }));
      return false;
    }
  };

  // Helper methods for workspace management
  const getWorkspace = (id: string): Workspace | undefined => {
    return get({ subscribe }).workspaces.get(id);
  };

  const setWorkspace = (workspace: Workspace) => {
    update((store) => {
      const newWorkspaces = new Map(store.workspaces);
      newWorkspaces.set(workspace.id, workspace);
      return { ...store, workspaces: newWorkspaces };
    });
  };

  const removeWorkspace = (id: string) => {
    update((store) => {
      const newWorkspaces = new Map(store.workspaces);
      newWorkspaces.delete(id);
      return { ...store, workspaces: newWorkspaces };
    });
  };

  const getAllWorkspaces = (): Workspace[] => {
    return Array.from(get({ subscribe }).workspaces.values());
  };

  const getWorkspacesArray = (): Readable<Workspace[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.workspaces.values());
    });
  };

  // Set selected workspace (background will be updated automatically via derived store)
  const setSelectedWorkspace = (workspaceId: string) => {
    update((store) => ({
      ...store,
      lastSelectedWorkspace:
        store.selectedWorkspace !== 'agenda' && store.selectedWorkspace !== 'new'
          ? store.selectedWorkspace
          : store.lastSelectedWorkspace,
      selectedWorkspace: workspaceId
    }));
  };

  // Get the last selected workspace
  const getLastSelectedWorkspace = (): string | undefined => {
    return get({ subscribe }).lastSelectedWorkspace;
  };

  // Get workspace color by ID, handling special cases
  const getWorkspaceColor = (workspaceId?: string): string | null => {
    if (!workspaceId || workspaceId === 'new') {
      return null;
    }

    // Special case for 'agenda' - return a default color
    if (workspaceId === 'agenda') {
      return '#3B82F6'; // Default blue color for agenda
    }

    const store = get({ subscribe });
    const workspace = store.workspaces.get(workspaceId);
    return workspace?.data?.color || null;
  };

  return {
    subscribe,
    refreshWorkspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    getWorkspace,
    setWorkspace,
    removeWorkspace,
    getAllWorkspaces,
    getWorkspacesArray,
    setSelectedWorkspace,
    getLastSelectedWorkspace,
    getWorkspaceColor
  };
};

export const workspaceStore = createWorkspaceStore();

// Derived store that provides workspace color variants for calendar events
export const workspaceColorVariants = derived([workspaceStore], ([$workspaceStore]) => {
  const selectedWorkspaceId = $workspaceStore.selectedWorkspace;
  const workspaceColor = workspaceStore.getWorkspaceColor(selectedWorkspaceId);

  if (!workspaceColor) {
    // Default colors if no workspace color
    return {
      gentleBgColor: '#EEF2FF', // bg-indigo-50 equivalent
      mediumBgColor: '#C7D2FE', // bg-indigo-200 equivalent
      strongBgColor: '#6366F1' // bg-indigo-500 equivalent
    };
  }

  return getWorkspaceColorVariants(workspaceColor);
});

// Helper function to convert hex to rgba (moved outside for reuse)
const hexToRgba = (hex: string, alpha: number): string => {
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) {
    return `rgba(107, 114, 128, ${alpha})`;
  }
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return `rgba(107, 114, 128, ${alpha})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Store to track current theme (dark or light)
export const currentTheme = writable<'light' | 'dark'>('light');

// Initialize theme detection
if (typeof window !== 'undefined' && window.matchMedia) {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  currentTheme.set(mediaQuery.matches ? 'dark' : 'light');

  // Listen for theme changes
  mediaQuery.addEventListener('change', (e) => {
    currentTheme.set(e.matches ? 'dark' : 'light');
  });
}

// Helper function to get default background based on theme
const getDefaultBackground = (theme: 'light' | 'dark'): string => {
  return theme === 'dark' ? 'rgba(9, 10, 11, 0.5)' : 'rgba(243, 244, 246, 0.5)';
};

// Derived store that computes background color based on workspace, pathname, and theme
export const workspaceBackgroundColor = derived(
  [workspaceStore, currentPathname, currentTheme],
  ([$workspaceStore, $currentPathname, $currentTheme]) => {
    // Clear background if not on home page
    if ($currentPathname && !$currentPathname.startsWith('/app/home')) {
      return getDefaultBackground($currentTheme);
    }

    // Extract workspace ID from pathname if available
    let workspaceId = $workspaceStore.selectedWorkspace;
    if ($currentPathname && $currentPathname.startsWith('/app/home/')) {
      const pathParts = $currentPathname.split('/');
      if (pathParts.length >= 3) {
        workspaceId = pathParts[2]; // /home/[workspaceId]/...
      }
    }

    if (!workspaceId || workspaceId === 'new') {
      return getDefaultBackground($currentTheme);
    }

    const workspace = $workspaceStore.workspaces.get(workspaceId);

    if (workspace?.data?.color) {
      return hexToRgba(workspace.data.color, 0.05);
    } else {
      // Default gray background for agenda or workspaces without custom colors
      return getDefaultBackground($currentTheme);
    }
  }
);

// Optimized background updater that only changes when color actually changes
let lastBackgroundColor = '';

export const updateBackgroundFromStore = () => {
  const unsubscribe = workspaceBackgroundColor.subscribe((color) => {
    if (color !== lastBackgroundColor) {
      lastBackgroundColor = color;
    }
  });

  return unsubscribe;
};

// Initialize background updater lazily to avoid test issues
let backgroundUpdateInitialized = false;
let backgroundUpdateUnsubscribe: (() => void) | null = null;

export const initializeBackgroundUpdater = () => {
  if (!backgroundUpdateInitialized && typeof window !== 'undefined') {
    backgroundUpdateInitialized = true;
    backgroundUpdateUnsubscribe = updateBackgroundFromStore();
  }
};

// Clean up on HMR to prevent memory leaks
if (typeof import.meta.hot !== 'undefined') {
  import.meta.hot?.dispose(() => {
    console.log('[HMR] Cleaning up workspace store');
    if (backgroundUpdateUnsubscribe) {
      backgroundUpdateUnsubscribe();
      backgroundUpdateUnsubscribe = null;
      backgroundUpdateInitialized = false;
    }
    // Clear workspace map to free memory
    const currentState = get(workspaceStore);
    currentState.workspaces.clear();
  });
}
